import React, { useEffect, useRef, useState } from 'react';

import { Modal } from 'flowbite';

import ExpenseFundApprovalViewModal from '../../expense_fund_approvals/ExpenseFundApprovalViewModal';
import PositionFundApprovalsViewModal from '../../position_fund_approvals/PositionFundApprovalsViewModal';
import PurchaseOrdersViewModal from '../../purchase_orders/PurchaseOrdersViewModal';
import PurchaseRequisitionsEditModal from '../../purchase_requisitions/PurchaseRequisitionsEditModal';
import PurchaseRequisitionsViewModal from '../../purchase_requisitions/PurchaseRequisitionsViewModal';
import UserRequestListExpenseFundApprovalsRow, { isExpenseFundApprovalPending } from '../../user/UserRequestListExpenseFundApprovalsRow';
import UserRequestListPositionFundApprovalsRow, { isPositionFundApprovalPending } from '../../user/UserRequestListPositionFundApprovalsRow';
import UserRequestListPurchaseOrdersRow, { isPurchaseOrderPending } from '../../user/UserRequestListPurchaseOrdersRow';
import UserRequestListPurchaseRequisitionsRow, {
    isPurchaseRequisitionPending,
    isPurchaseRequisitionRejected,
} from '../../user/UserRequestListPurchaseRequisitionsRow';

import QueueFilterChips from './QueueFilterChips';
import QueueTable from './QueueTable';
import { TYPE_EXPENSE_FUND, TYPE_POSITION_FUND, TYPE_PURCHASE_ORDER, TYPE_PURCHASE_REQUISITION } from './queueTypes';
import useHomeQueue from './useHomeQueue';

import { modalOptions } from 'Helpers/modalOptions';

import ConfirmationModal from 'CommonComponents/ConfirmationModal';

/* Home's exception wash, reused for a returned row. `!` because .inbud-table
   sets its own row background. The server has already sorted returned rows to
   the top; this is what makes that visible. */
const RETURNED_ROW_CLASS = '!tw-bg-red-10';

/**
 * Every request this user raised, in one list, returned ones pinned on top.
 *
 * NOT open-only, deliberately: returned, in-approval, approved and cancelled
 * requests are here together, because the raiser needs to find the one they
 * filed whatever became of it. That is why this list's total is larger than the
 * "Your requests" tile, which counts open ones only, and nothing here tries to
 * reconcile the two.
 *
 * Editing and cancelling reuse the per-type modals and their endpoints
 * unchanged; the cancel confirmation's wording is per type, which is why the
 * type is carried alongside the record while a confirmation is open.
 */
export default function MyRequestsView({ requestsQueueUrl, onQueueChange, myViews }) {
    const queue = useHomeQueue(requestsQueueUrl);

    const refs = {
        prView: useRef(),
        prEdit: useRef(),
        poView: useRef(),
        pfaView: useRef(),
        efaView: useRef(),
        cancel: useRef(),
    };

    const [modals, setModals] = useState({});

    useEffect(() => {
        setModals(
            Object.fromEntries(
                Object.entries(refs)
                    .filter(([, ref]) => !!ref.current)
                    .map(([name, ref]) => [name, new Modal(ref.current, modalOptions(false))]),
            ),
        );
    }, []);

    const [active, setActive] = useState({ modal: null, row: null });

    useEffect(() => {
        if (active.modal && modals[active.modal]) {
            modals[active.modal].show();
        }
    }, [active, modals]);

    /* ConfirmationModal drives its own request, so its three outcome flags live
       here the way they do in each per-type list. */
    const [cancelState, setCancelState] = useState({ inProgress: false, completed: false, failed: false });

    const open = (modal, row) => setActive({ modal, row });

    const close = () => {
        Object.values(modals).forEach((modal) => modal.hide());
        setActive({ modal: null, row: null });
    };

    const afterAction = () => {
        close();
        queue.refresh();
        onQueueChange?.();
    };

    /* The cancel endpoint and its wording, per type. Each URL is the one the
       per-type tab already posts to, carrying the same record-id placeholder. */
    const CANCELS = {
        [TYPE_PURCHASE_REQUISITION]: {
            url: myViews.cancel_pr_url,
            placeholder: 'REQUISITION_ID',
            title: $t('Cancellation of Purchase Requisition'),
            message: (row) => $t('Cancel Purchase Requisition') + ' ' + (row.record.pr_code ?? ''),
            completed: $t('Purchase Requisition canceled'),
            failed: $t('Purchase Requisition could not be canceled'),
        },
        [TYPE_PURCHASE_ORDER]: {
            url: myViews.cancel_po_url,
            placeholder: 'ORDER_ID',
            title: $t('Cancellation of Expenditure Approval'),
            message: () => $t('Cancel the Expenditure Approval'),
            completed: $t('Expenditure Approval canceled'),
            failed: $t('Expenditure Approval could not be canceled'),
        },
        [TYPE_POSITION_FUND]: {
            url: myViews.cancel_position_fund_approval_url,
            placeholder: 'POSITION_FUND_APPROVAL_ID',
            title: $t('Cancellation of Position Fund Approval'),
            message: () => $t('Cancel the Position Fund Approval'),
            completed: $t('Position Fund Approval canceled'),
            failed: $t('Position Fund Approval could not be canceled'),
        },
        [TYPE_EXPENSE_FUND]: {
            url: myViews.cancel_expense_fund_approval_url,
            placeholder: 'EXPENSE_FUND_APPROVAL_ID',
            title: $t('Cancellation of Expense Fund Approval'),
            message: () => $t('Cancel the Expense Fund Approval'),
            completed: $t('Expense Fund Approval canceled'),
            failed: $t('Expense Fund Approval could not be canceled'),
        },
    };

    const cancelling = active.modal === 'cancel' ? active.row : null;
    const cancelConfig = cancelling ? CANCELS[cancelling.type] : null;

    const triggerCancel = () => {
        setCancelState({ inProgress: true, completed: false, failed: false });

        axios
            .put(cancelConfig.url.replace(cancelConfig.placeholder, cancelling.id))
            .then(() => setCancelState({ inProgress: false, completed: true, failed: false }))
            .catch(() => setCancelState({ inProgress: false, completed: false, failed: true }));
    };

    const columns = [
        { key: 'date', label: $t('Date'), className: 'tw-w-24' },
        { key: 'type', label: $t('Type'), className: 'tw-w-44' },
        { key: 'title', label: $t('Title') },
        { key: 'value', label: $t('Value'), className: 'tw-w-32' },
        { key: 'status', label: $t('Status'), className: 'tw-w-36' },
        { key: 'actions', label: '', className: 'tw-w-28' },
    ];

    /* "Still open" is per type, and every one of these is the SHIPPED predicate
       imported from the type's own row module — not a second copy of it. They
       gate the Cancel button, this page has no test coverage, and a copy here
       would be one a later edit to the original never reaches.

       All four compare `status`, never `status_label`. The pending constants the
       Blade publishes are the untranslated values, so comparing the localised
       label would go false under any non-English locale and take the Cancel
       button away from exactly those users. */
    const isOpen = (type, record) =>
        ({
            [TYPE_PURCHASE_REQUISITION]: () => isPurchaseRequisitionPending(record, myViews.pending_pr_status),
            [TYPE_PURCHASE_ORDER]: () => isPurchaseOrderPending(record, myViews.pending_po_status),
            [TYPE_POSITION_FUND]: () => isPositionFundApprovalPending(record, myViews.pending_pfa_status),
            [TYPE_EXPENSE_FUND]: () => isExpenseFundApprovalPending(record, myViews.pending_efa_status),
        })[type]?.() ?? false;

    const renderRow = (row) => {
        const shared = {
            key: `${row.type}:${row.id}`,
            row: row.record,
            typeLabel: row.type_label,
            showValue: true,
            rowClassName: row.returned ? RETURNED_ROW_CLASS : undefined,
            /* Cancel is offered on a row that is still open. The unified list
               carries closed requests too, so this is per row rather than per
               list. */
            isPending: (record) => !record.canceled_at && isOpen(row.type, record),
            onCancel: () => open('cancel', row),
        };

        switch (row.type) {
            case TYPE_PURCHASE_REQUISITION:
                return (
                    <UserRequestListPurchaseRequisitionsRow
                        {...shared}
                        isRejected={(record) => isPurchaseRequisitionRejected(record, myViews.approval_pr_statuses?.rejected)}
                        onView={() => open('prView', row)}
                        onEdit={() => open('prEdit', row)}
                    />
                );
            case TYPE_PURCHASE_ORDER:
                return <UserRequestListPurchaseOrdersRow {...shared} onView={() => open('poView', row)} />;
            case TYPE_POSITION_FUND:
                return <UserRequestListPositionFundApprovalsRow {...shared} onView={() => open('pfaView', row)} />;
            case TYPE_EXPENSE_FUND:
                return <UserRequestListExpenseFundApprovalsRow {...shared} onView={() => open('efaView', row)} />;
            default:
                return null;
        }
    };

    const record = active.row?.record ?? {};

    return (
        <div>
            <QueueFilterChips countsByType={queue.countsByType} selected={queue.type} onSelect={queue.filterByType} />

            <QueueTable
                columns={columns}
                rows={queue.rows}
                renderRow={renderRow}
                loading={queue.loading}
                error={queue.error}
                total={queue.queue?.total ?? 0}
                from={queue.queue?.from}
                to={queue.queue?.to}
                lastPage={queue.queue?.last_page ?? 1}
                page={queue.page}
                onPageChange={queue.setPage}
                emptyState={
                    <div className="tw-py-8 tw-text-center">
                        <p className="tw-text-sm tw-font-semibold tw-text-charcoal">{$t('You have not raised any requests')}</p>
                        <p className="tw-mt-1 tw-text-xs tw-text-charcoal-60">{$t('Requests you file will appear here until they are closed.')}</p>
                    </div>
                }
            />

            <PurchaseRequisitionsViewModal
                modalId="home-request-view-pr"
                modalRef={refs.prView}
                modalElement={modals.prView}
                closeHandler={close}
                successHandler={close}
                pr={active.modal === 'prView' ? record : {}}
                displayApprovalStatuses={myViews.display_approval_statuses}
                openRecordUrl={active.modal === 'prView' ? active.row?.href : null}
            />
            <PurchaseRequisitionsEditModal
                modalId="home-request-edit-pr"
                modalRef={refs.prEdit}
                modalElement={modals.prEdit}
                closeHandler={close}
                successHandler={afterAction}
                pr={active.modal === 'prEdit' ? record : {}}
                policies={myViews.policies}
                updateUrl={myViews.update_pr_url}
            />
            <PurchaseOrdersViewModal
                modalId="home-request-view-po"
                modalRef={refs.poView}
                modalElement={modals.poView}
                closeHandler={close}
                successHandler={close}
                po={active.modal === 'poView' ? record : {}}
                displayApprovalStatuses={myViews.display_approval_statuses}
                openRecordUrl={active.modal === 'poView' ? active.row?.href : null}
            />
            <PositionFundApprovalsViewModal
                modalId="home-request-view-pfa"
                modalRef={refs.pfaView}
                modalElement={modals.pfaView}
                closeHandler={close}
                successHandler={close}
                pfa={active.modal === 'pfaView' ? record : {}}
                displayApprovalStatuses={myViews.display_approval_statuses}
            />
            <ExpenseFundApprovalViewModal
                modalId="home-request-view-efa"
                modalRef={refs.efaView}
                modalElement={modals.efaView}
                closeHandler={close}
                successHandler={close}
                efa={active.modal === 'efaView' ? record : {}}
                displayApprovalStatuses={myViews.display_approval_statuses}
            />

            <ConfirmationModal
                modalId="home-request-cancel"
                modalRef={refs.cancel}
                modalElement={modals.cancel}
                closeHandler={close}
                successHandler={afterAction}
                triggerAction={triggerCancel}
                cleanStates={() => setCancelState({ inProgress: false, completed: false, failed: false })}
                allowAction={!!cancelling}
                request={cancelState.inProgress}
                actionCompleted={cancelState.completed}
                actionFailed={cancelState.failed}
                title={cancelConfig?.title ?? ''}
                confirmationMsg={cancelling ? cancelConfig.message(cancelling) : ''}
                actionCompletedMsg={cancelConfig?.completed ?? ''}
                actionFailedMsg={cancelConfig?.failed ?? ''}
                cancelButtonText={$t('Close')}
                primaryButtonText={$t('Cancel request')}
            />
        </div>
    );
}
